import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormArray, NonNullableFormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AppButtonComponent } from '../../../shared/components/button/app-button.component';
import { SubscriptionService, SubscriptionModal } from '../../../core/services/subscription.service';
import { GroupSubscriptionService } from '../../../core/services/group-subscription.service';
import { UiService } from '../../../core/services/ui.service';
import { UserService, User } from '../../../core/services/user.service';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { QuillModule } from 'ngx-quill';
import { logCritical } from '../../../core/logging/logger.service';
import { getApiErrorMessage } from '../../../core/helpers/http-error.helper';
import {
  GROUP_BENEFIT_ICON_KEYS,
  groupBenefitIconIncludesPlate,
  groupBenefitIconSrc,
} from '../../../core/helpers/group-benefit.helper';

@Component({
  selector: 'app-add-membership',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppButtonComponent, QuillModule],
  templateUrl: './add-membership.component.html',
  styleUrls: ['./add-membership.component.scss'],
})
export class AddMembershipComponent implements OnInit, OnDestroy {
  @ViewChild('editor', { static: false }) editor: unknown;
  isVisible = false;
  isLoading = false;
  errorMessage = '';
  isEditMode = false;
  subscriptionId: string | null = null;
  isLocked = false;

  /** Plan type: 'b2c', 'b2b', or 'b2c-promotion' */
  planType: 'b2c' | 'b2b' | 'b2c-promotion' = 'b2c';

  /** B2B manage modal wizard step (1 = base fields, 2 = benefits). */
  b2bStep: 1 | 2 = 1;

  /** Promotional set index for b2c-promotion plans */
  promotionalSetIndex = 0;

  activeUsers: User[] = [];
  private subscriptions = new Subscription();
  addMembershipForm: FormGroup;
  openIconPickerIndex: number | null = null;
  readonly benefitIconKeys = GROUP_BENEFIT_ICON_KEYS;

  editorModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic'],
      [{ list: 'bullet' }, { list: 'ordered' }],
      ['image'],
    ],
  };

  constructor(
    private uiService: UiService,
    private subscriptionService: SubscriptionService,
    private groupSubscriptionService: GroupSubscriptionService,
    private userService: UserService,
    private fb: NonNullableFormBuilder,
    private snackbarService: SnackbarService,
  ) {
    this.addMembershipForm = this.fb.group({
      planType: this.fb.control<'b2c' | 'b2b' | 'b2c-promotion'>('b2c', [Validators.required]),
      name: this.fb.control('', [Validators.required]),
      subtitle: this.fb.control(''),
      description: this.fb.control('', [Validators.required]),
      stripePricingLink: this.fb.control('', [Validators.required]),
      stripePaymentLink: this.fb.control('', [Validators.required]),
      billingCycle: this.fb.control('Month', [Validators.required]),
      isPublished: this.fb.control(false),
      trialDays: this.fb.control(30, [Validators.required, Validators.min(0)]),
      seatCount: this.fb.control(1, [Validators.required, Validators.min(1)]),
      durationMonths: this.fb.control(12, [Validators.required, Validators.min(1)]),
      benefits: this.fb.array([]),
    });
  }

  onEditorContentChanged(_event: unknown): void {
    const editorElem = document.querySelector('.ql-editor') as HTMLElement;
    if (editorElem) {
      editorElem.style.height = 'auto';
      editorElem.style.height = editorElem.scrollHeight / 16 + 'rem';
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.uiService.addMembershipModal$.subscribe((data) => {
        if (data === true) {
          this.openForCreate('b2c');
        } else if (typeof data === 'object' && data !== null) {
          if ('planType' in data) {
            const modalData = data as { planType: 'b2c' | 'b2b' | 'b2c-promotion'; subscription?: SubscriptionModal; promotionalSetIndex?: number };
            this.promotionalSetIndex = modalData.promotionalSetIndex ?? 0;
            if (modalData.subscription) {
              this.openForEdit(modalData.subscription, modalData.planType);
            } else {
              this.openForCreate(modalData.planType);
            }
          } else {
            this.openForEdit(data as SubscriptionModal, 'b2c');
          }
        }
      }),
    );
  }

  openForCreate(planType: 'b2c' | 'b2b' | 'b2c-promotion' = 'b2c'): void {
    this.isEditMode = false;
    this.subscriptionId = null;
    this.isLocked = false;
    this.planType = planType;
    this.b2bStep = 1;
    this.isVisible = true;

    this.addMembershipForm.reset({
      planType: planType,
      subtitle: '',
      billingCycle: planType === 'b2b' ? 'B2B' : 'Month',
      isPublished: false,
      trialDays: 30,
      seatCount: 1,
      durationMonths: 12,
    });

    this.updateFormForPlanType();
    if (this.planType === 'b2b') {
      this.resetBenefits();
      this.toggleFieldLock(false);
    } else {
      this.clearBenefits();
      this.toggleFieldLock(false);
    }
  }

  openForEdit(subscription: SubscriptionModal, planType?: 'b2c' | 'b2b' | 'b2c-promotion'): void {
    this.isEditMode = true;
    this.subscriptionId = subscription.subscriptionId;
    this.isLocked = false;
    this.b2bStep = 1;
    this.planType =
      planType ||
      subscription.planType ||
      (subscription.isPromotion ? 'b2c-promotion' : subscription.billingCycle === 'B2B' ? 'b2b' : 'b2c');

    const formValue = {
      planType: this.planType,
      name: subscription.name,
      subtitle: subscription.subtitle ?? '',
      description: subscription.description,
      stripePricingLink: subscription.stripePricingLink,
      stripePaymentLink: subscription.stripePaymentLink,
      billingCycle: subscription.billingCycle,
      isPublished: subscription.isPublished,
      trialDays: subscription.trialDays ?? 30,
    };

    this.addMembershipForm.patchValue(formValue);
    this.addMembershipForm.get('planType')?.disable();
    this.updateFormForPlanType();

    if (this.planType === 'b2b') {
      this.resetBenefits(subscription.benefits);
    } else {
      this.clearBenefits();
    }

    if (this.planType === 'b2c' || this.planType === 'b2c-promotion') {
      this.userService.getActiveUsersWithValidSubscriptions().subscribe({
        next: (users) => {
          const inUse = users
            .filter((u) => u.userStatus === true)
            .some((u) => u.subscriptionId === subscription.subscriptionId);

          this.toggleFieldLock(inUse);
          this.isVisible = true;
        },
        error: (err) => {
          logCritical('components/subscriptions/add-membership/add-membership.component', 'Error loading active users:', err);
          this.toggleFieldLock(false);
          this.isVisible = true;
        },
      });
    } else {
      this.toggleFieldLock(true);
      this.isVisible = true;
    }
  }

  private updateFormForPlanType(): void {
    const nameControl = this.addMembershipForm.get('name');
    const descriptionControl = this.addMembershipForm.get('description');
    const stripePricingLinkControl = this.addMembershipForm.get('stripePricingLink');
    const stripePaymentLinkControl = this.addMembershipForm.get('stripePaymentLink');
    const seatCountControl = this.addMembershipForm.get('seatCount');
    const durationMonthsControl = this.addMembershipForm.get('durationMonths');
    const billingCycleControl = this.addMembershipForm.get('billingCycle');
    const trialDaysControl = this.addMembershipForm.get('trialDays');

    if (this.planType === 'b2b') {
      nameControl?.setValidators([Validators.required]);
      descriptionControl?.setValidators([Validators.required]);
      stripePricingLinkControl?.setValidators([Validators.required]);
      stripePaymentLinkControl?.clearValidators();
      seatCountControl?.clearValidators();
      durationMonthsControl?.clearValidators();
      billingCycleControl?.clearValidators();
      billingCycleControl?.setValue('B2B');
      trialDaysControl?.clearValidators();
    } else if (this.planType === 'b2c-promotion') {
      nameControl?.setValidators([Validators.required]);
      descriptionControl?.setValidators([Validators.required]);
      stripePricingLinkControl?.setValidators([Validators.required]);
      stripePaymentLinkControl?.setValidators([Validators.required]);
      billingCycleControl?.setValidators([Validators.required]);
      seatCountControl?.clearValidators();
      durationMonthsControl?.clearValidators();
      trialDaysControl?.setValidators([Validators.required, Validators.min(0)]);
    } else {
      nameControl?.setValidators([Validators.required]);
      descriptionControl?.setValidators([Validators.required]);
      stripePricingLinkControl?.setValidators([Validators.required]);
      stripePaymentLinkControl?.setValidators([Validators.required]);
      billingCycleControl?.setValidators([Validators.required]);
      seatCountControl?.clearValidators();
      durationMonthsControl?.clearValidators();
      trialDaysControl?.setValidators([Validators.required, Validators.min(0)]);
    }

    nameControl?.updateValueAndValidity();
    descriptionControl?.updateValueAndValidity();
    stripePricingLinkControl?.updateValueAndValidity();
    stripePaymentLinkControl?.updateValueAndValidity();
    seatCountControl?.updateValueAndValidity();
    durationMonthsControl?.updateValueAndValidity();
    billingCycleControl?.updateValueAndValidity();
    trialDaysControl?.updateValueAndValidity();
  }

  get showTrialDays(): boolean {
    return this.planType === 'b2c' || this.planType === 'b2c-promotion';
  }

  get modalTitle(): string {
    if (this.planType === 'b2b' && this.isEditMode) return 'Manage Membership';
    return this.isEditMode ? 'Modify Membership' : 'Add new Membership';
  }

  get benefitsArray(): FormArray {
    return this.addMembershipForm.get('benefits') as FormArray;
  }

  addBenefit(icon = 'book', description = ''): void {
    this.benefitsArray.push(
      this.fb.group({
        icon: this.fb.control(icon, [Validators.required]),
        description: this.fb.control(description),
      }),
    );
    this.openIconPickerIndex = null;
  }

  removeBenefit(index: number): void {
    if (this.benefitsArray.length <= 1) return;
    this.benefitsArray.removeAt(index);
    this.openIconPickerIndex = null;
  }

  toggleIconPicker(index: number): void {
    this.openIconPickerIndex = this.openIconPickerIndex === index ? null : index;
  }

  selectBenefitIcon(index: number, icon: string): void {
    this.benefitsArray.at(index).get('icon')?.setValue(icon);
    this.openIconPickerIndex = null;
  }

  benefitIconSrc(icon: string): string {
    return groupBenefitIconSrc(icon);
  }

  benefitIconIncludesPlate(icon: string): boolean {
    return groupBenefitIconIncludesPlate(icon);
  }

  private resetBenefits(benefits?: { icon: string; description: string }[]): void {
    this.clearBenefits();
    const items = benefits?.length ? benefits : [{ icon: 'book', description: '' }];
    for (const item of items) {
      this.addBenefit(item.icon || 'book', item.description || '');
    }
  }

  private clearBenefits(): void {
    while (this.benefitsArray.length) {
      this.benefitsArray.removeAt(0);
    }
    this.openIconPickerIndex = null;
  }

  private toggleFieldLock(shouldLock: boolean): void {
    this.isLocked = shouldLock;

    const fieldsToLock = ['stripePricingLink', 'stripePaymentLink', 'billingCycle'];
    fieldsToLock.forEach((field) => {
      const control = this.addMembershipForm.get(field);
      if (control) {
        shouldLock ? control.disable() : control.enable();
      }
    });
  }

  closeModal(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.b2bStep = 1;
    this.isVisible = false;
    this.uiService.closeAddMembershipModal();
  }

  onB2bContinue(): void {
    this.markB2bStep1Touched();
    if (this.isB2bStep1Invalid) return;
    this.b2bStep = 2;
    this.errorMessage = '';
  }

  onB2bBack(): void {
    this.b2bStep = 1;
    this.errorMessage = '';
  }

  private markB2bStep1Touched(): void {
    ['name', 'description', 'stripePricingLink'].forEach((field) => {
      this.addMembershipForm.get(field)?.markAsTouched();
    });
  }

  onSubmit(): void {
    if (this.addMembershipForm.invalid) return;
    this.isLoading = true;
    this.errorMessage = '';
    const formValue = this.addMembershipForm.getRawValue();

    if (this.planType === 'b2b') {
      const b2bData = {
        name: formValue.name,
        description: formValue.description,
        stripePricingLink: formValue.stripePricingLink,
        stripePaymentLink: formValue.stripePaymentLink || undefined,
        isPublished: formValue.isPublished,
        benefits: (formValue.benefits as { icon: string; description: string }[] | undefined)?.filter(
          (b) => (b.description ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0,
        ) ?? [],
      };

      if (this.isEditMode && this.subscriptionId) {
        this.groupSubscriptionService.editGroupSubscription(this.subscriptionId, b2bData).subscribe({
          next: () => this.handleSuccess(),
          error: (err) => this.handleError(err, 'updating'),
        });
      } else {
        this.groupSubscriptionService.createGroupSubscription(b2bData).subscribe({
          next: () => this.handleSuccess(),
          error: (err) => this.handleError(err, 'creating'),
        });
      }
    } else {
      const b2cData = {
        name: formValue.name,
        description: formValue.description,
        subtitle: formValue.subtitle,
        stripePricingLink: formValue.stripePricingLink,
        stripePaymentLink: formValue.stripePaymentLink,
        billingCycle: formValue.billingCycle,
        isPublished: formValue.isPublished,
        trialDays: formValue.trialDays ?? 30,
        isPromotion: this.planType === 'b2c-promotion',
        promotionalSetIndex: this.planType === 'b2c-promotion' ? this.promotionalSetIndex : 0,
      };

      if (this.isEditMode && this.subscriptionId) {
        this.subscriptionService.editSubscription(this.subscriptionId, b2cData).subscribe({
          next: () => this.handleSuccess(),
          error: (err) => this.handleError(err, 'updating'),
        });
      } else {
        this.subscriptionService.createSubscription(b2cData).subscribe({
          next: () => this.handleSuccess(),
          error: (err) => this.handleError(err, 'creating'),
        });
      }
    }
  }

  private handleSuccess(): void {
    this.isLoading = false;
    this.b2bStep = 1;
    this.addMembershipForm.reset();
    this.closeModal();
    this.uiService.triggerSubscriptionReload();
    this.snackbarService.show(
      this.isEditMode ? 'Membership successfully updated.' : 'Membership successfully created.',
      'success',
    );
  }

  private handleError(err: unknown, action: string): void {
    const errorText = getApiErrorMessage(err, `Error ${action} subscription`);

    this.isLoading = false;
    this.errorMessage = errorText;
    this.snackbarService.show(`Error ${action} subscription: ${errorText}`, 'error');
    logCritical('components/subscriptions/add-membership/add-membership.component', `Error ${action} subscription:`, err);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isFormInvalid(): boolean {
    return this.addMembershipForm.invalid;
  }

  get isB2bStep1Invalid(): boolean {
    if (this.planType !== 'b2b') return false;
    return (
      !!this.addMembershipForm.get('name')?.invalid ||
      !!this.addMembershipForm.get('description')?.invalid ||
      !!this.addMembershipForm.get('stripePricingLink')?.invalid
    );
  }

  resizeTextarea(event: MouseEvent): void {
    event.preventDefault();

    const container = document.querySelector('.ql-container') as HTMLElement;
    if (!container) return;

    const startY = event.clientY;
    const startHeight = container.offsetHeight;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newHeight = startHeight + (moveEvent.clientY - startY);
      container.style.height = `${newHeight}px`;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }
}